/**
 * WeaveIn.ai Platform - Medicaid Enrollment Status Widget
 * Displays current Medicaid enrollment status in member profile
 *
 * MIGRATED: MUI -> Tailwind CSS components
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Stethoscope, Plus, RefreshCw, AlertTriangle, CheckCircle, Info, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  medicaidEnrollmentAPI,
  MedicaidEnrollment,
  ENROLLMENT_STATUS_LABELS,
  ENROLLMENT_STATUS_COLORS,
  WORK_REQUIREMENT_STATUS_LABELS,
  WORK_REQUIREMENT_STATUS_COLORS,
  getDaysUntilRedetermination,
  isRedeterminationDueSoon,
  isRedeterminationOverdue,
} from '@/services/api/medicaidEnrollment';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface EnrollmentStatusWidgetProps {
  memberId: number;
  onStartEnrollment?: () => void;
  onViewEnrollment?: (enrollment: MedicaidEnrollment) => void;
}

// Map MUI color names to Tailwind badge variants
const getStatusBadgeVariant = (muiColor: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (muiColor) {
    case 'success':
      return 'default';
    case 'error':
      return 'destructive';
    case 'warning':
    case 'info':
      return 'secondary';
    default:
      return 'outline';
  }
};

// Custom status badge colors using Tailwind classes
const getStatusClassName = (muiColor: string): string => {
  switch (muiColor) {
    case 'success':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-200 dark:border-green-700';
    case 'error':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 dark:border-yellow-700';
    case 'info':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-700';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600';
  }
};

export default function EnrollmentStatusWidget({
  memberId,
  onStartEnrollment,
  onViewEnrollment,
}: EnrollmentStatusWidgetProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [enrollment, setEnrollment] = useState<MedicaidEnrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEnrollment();
  }, [memberId]);

  const loadEnrollment = async () => {
    setLoading(true);
    setError(null);
    try {
      const enrollments = await medicaidEnrollmentAPI.getMemberEnrollments(memberId);

      // Find active or most recent enrollment
      const activeEnrollment = enrollments.find(
        (e) => e.status === 'ACTIVELY_ENROLLED' || e.status === 'REACTIVATED' || e.status === 'PENDING_REDETERMINATION'
      );

      if (activeEnrollment) {
        setEnrollment(activeEnrollment);
      } else if (enrollments.length > 0) {
        // Get most recent enrollment
        setEnrollment(enrollments[0]);
      } else {
        setEnrollment(null);
      }
    } catch (err: any) {
      console.error('Error loading enrollment:', err);
      setError(err.message || 'Failed to load enrollment');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadEnrollment();
  };

  const getAlertForRedetermination = () => {
    if (!enrollment?.next_redetermination_date) return null;

    if (isRedeterminationOverdue(enrollment.next_redetermination_date)) {
      return (
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('members.redetermination_overdue', 'Re-determination Overdue')}</strong>
            <br />
            {t('members.coverage_may_be_at_risk', 'Coverage may be at risk. Contact member immediately to schedule re-determination.')}
          </AlertDescription>
        </Alert>
      );
    }

    if (isRedeterminationDueSoon(enrollment.next_redetermination_date)) {
      const days = getDaysUntilRedetermination(enrollment.next_redetermination_date);
      return (
        <Alert className="mt-4 border-yellow-200 bg-yellow-50 text-yellow-800">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('members.redetermination_due_soon', 'Re-determination Due Soon')}</strong>
            <br />
            Due in {days} day{days !== 1 ? 's' : ''}. Schedule appointment with member.
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t('members.medicaid_enrollment', 'Medicaid Enrollment')}</h3>
          </div>
          <div className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t('members.medicaid_enrollment_1', 'Medicaid Enrollment')}</h3>
            <div className="flex-1" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleRefresh}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Refresh</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // No enrollment case
  if (!enrollment) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t('members.medicaid_enrollment_2', 'Medicaid Enrollment')}</h3>
            <div className="flex-1" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleRefresh}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Refresh</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('members.no_active_medicaid_enrollment_found', 'No active Medicaid enrollment found for this member.')}
            </AlertDescription>
          </Alert>

          {onStartEnrollment && (
            <Button
              className="w-full mt-4"
              onClick={onStartEnrollment}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('members.start_enrollment_application', 'Start Enrollment Application')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Has enrollment case
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Stethoscope className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{t('members.medicaid_enrollment_3', 'Medicaid Enrollment')}</h3>
          <div className="flex-1" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="space-y-4">
          {/* Enrollment Number */}
          <div>
            <p className="text-xs text-muted-foreground">
              {t('members.enrollment_number', 'Enrollment Number')}
            </p>
            <p className="text-sm font-medium">
              {enrollment.enrollment_number}
            </p>
          </div>

          <hr className="border-border" />

          {/* Status */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {t('members.status', 'Status')}
            </p>
            <Badge
              className={`${getStatusClassName(ENROLLMENT_STATUS_COLORS[enrollment.status])} inline-flex items-center gap-1`}
            >
              {(enrollment.status === 'ACTIVELY_ENROLLED' || enrollment.status === 'REACTIVATED') && (
                <CheckCircle className="h-3 w-3" />
              )}
              {ENROLLMENT_STATUS_LABELS[enrollment.status]}
            </Badge>
          </div>

          {/* Application Date */}
          <div>
            <p className="text-xs text-muted-foreground">
              {t('members.application_date', 'Application Date')}
            </p>
            <p className="text-sm">
              {fmt.short(enrollment.application_date)}
            </p>
          </div>

          {/* Approval Date */}
          {enrollment.approval_date && (
            <div>
              <p className="text-xs text-muted-foreground">
                {t('members.approval_date', 'Approval Date')}
              </p>
              <p className="text-sm">
                {fmt.short(enrollment.approval_date)}
              </p>
            </div>
          )}

          {/* Work Requirement Status */}
          {enrollment.work_requirement_status && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {t('members.work_requirements', 'Work Requirements')}
              </p>
              <Badge
                variant="outline"
                className={getStatusClassName(WORK_REQUIREMENT_STATUS_COLORS[enrollment.work_requirement_status])}
              >
                {WORK_REQUIREMENT_STATUS_LABELS[enrollment.work_requirement_status]}
              </Badge>
            </div>
          )}

          {/* Next Re-determination */}
          {enrollment.next_redetermination_date && (
            <div>
              <p className="text-xs text-muted-foreground">
                {t('members.next_redetermination', 'Next Re-determination')}
              </p>
              <p className="text-sm">
                {fmt.short(enrollment.next_redetermination_date)}
              </p>
            </div>
          )}

          {/* Re-determination Alert */}
          {getAlertForRedetermination()}

          {/* View Details Button */}
          {onViewEnrollment && (
            <>
              <hr className="border-border" />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onViewEnrollment(enrollment)}
              >
                {t('members.view_full_details', 'View Full Details')}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
